/**
 * @typedef { import('../Dropdown').PanelOption } PanelOption
 *
 * @typedef PreselectedParams
 * @prop {PanelOption[]} options
 * @prop {string[] | number[]} preSelectedIds
 * @prop {string[]} preSelectedNames
 * @prop {boolean} preSelectFirstOption
 * @prop {PanelOption[]} selectedOptions
 */

/**
 * Deeply compares as nested object returning only those
 * which have a given key which matches one of the values in the given array
 * @param {PanelOption[] | Object} items,
 * @param {(string|number)[]} array,
 * @param {string} key
 * @returns {PanelOption[]}
 */
export const reduceOptionsBy = (items, array, key) =>
  items.reduce((acc, item) => {
    if (item.child) {
      const options = reduceOptionsBy(item.child, array, key)[0];
      // @ts-ignore
      if (options) acc.push(options);
    }

    // @ts-ignore
    const matchedItem = array.reduce((matchedItems, value) => {
      // @ts-ignore
      if (item[key] === value) matchedItems.push(item);
      return matchedItems;
    }, [])[0];

    // @ts-ignore
    if (matchedItem) acc.push(matchedItem);

    return acc;
  }, []);

/**
 * Returns initial selection of defined preselected items based on options.
 * @param {PreselectedParams} params
 * @returns {PanelOption[]}
 */
export const getPreSelected = ({
  options,
  preSelectFirstOption,
  preSelectedIds,
  preSelectedNames,
  selectedOptions,
}) => {
  if (preSelectFirstOption) {
    return [options[0]];
  }
  if (preSelectedIds) {
    return reduceOptionsBy(options, preSelectedIds, 'id');
  }
  if (preSelectedNames) {
    return reduceOptionsBy(options, preSelectedNames, 'name');
  }
  if (selectedOptions) {
    return reduceOptionsBy(
      options,
      selectedOptions.map((o) => o.id),
      'id'
    );
  }
  return [];
};

/**
 * Retrieve full list objects when only id is provided on form value.
 *
 * @param {PanelOption[]} value The value prop on the Dropdown field
 * @param {PanelOption[]} options The full list of options
 *
 * @returns {[PanelOption[], boolean]} [selectedOptions, isMutated]
 *  First value is the full object list. Second value indicates if data was mutated or not.
 */
export const getPreSelectedFormData = (value, options) => {
  // Check first value for 'name' property.
  // If it doesn't exist, we need to retrieve the full object
  if (value && value[0] && value[0].id && !value[0].name) {
    const preSelectedIds = value.map((val) => val.id);
    // @ts-ignore
    return [reduceOptionsBy(options, preSelectedIds, 'id'), true];
  }

  return [value, false];
};
